import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type OnChangeFn, type SortingState } from '@tanstack/react-table';
import { useQuery, usePaginatedQuery, useConvex, useAction, useMutation } from 'convex/react';
import type { Id } from 'convex/_generated/dataModel';
import { api } from '@/lib/api';
import {
  AccountingOverviewCards,
  TransactionsTable,
  InvoicesTable,
  MissingPlatformInvoices,
  WorkInvoicesTable,
  AccountingFilters,
  HideBookingDialog,
  type FilterValues,
  type BookingVisibilityDialogMode,
  type HideBookingReasonCode,
  type RestoreBookingReasonCode,
} from '@/components/features/operator/accounting';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/ui/icons';
import { Download } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from '@/template/lib/toast';
import { type PaymentStatus } from '@/types/validators';
import { type AccountingTabType, type AccountingExportType } from '@/types/operator';
import type { AccountingTransactionSortOption, Transaction } from '@/types/payments';
import type { PlatformInvoiceSortOption, WorkInvoice, WorkInvoiceSortOption } from '@/types/invoices';
import { didSortingChange, mapSortingToSortKey, type SortMapping } from '@/lib/data-table-state';
import { useTranslation } from 'react-i18next';
import { dateKeyToBerlinReference, endOfBerlinDay, startOfBerlinDay } from '@/lib/berlin-time';
import { reconcileAccountingPayment } from '@/components/features/operator/accounting/reconcile-payment';

type BookingVisibilityDialogTarget =
  | {
      type: 'transaction';
      mode: BookingVisibilityDialogMode;
      transaction: Transaction;
    }
  | {
      type: 'workInvoice';
      mode: BookingVisibilityDialogMode;
      invoice: WorkInvoice;
    };

const transactionSortColumns = ['createdAt', 'amount', 'status', 'paymentType', 'paymentMethod'] as const;
type TransactionSortColumn = (typeof transactionSortColumns)[number];
const transactionSortMapping: SortMapping<TransactionSortColumn, AccountingTransactionSortOption> = {
  createdAt: { asc: 'created_at_asc', desc: 'created_at_desc' },
  amount: { asc: 'amount_asc', desc: 'amount_desc' },
  status: { asc: 'status_asc', desc: 'status_desc' },
  paymentType: { asc: 'payment_type_asc', desc: 'payment_type_desc' },
  paymentMethod: { asc: 'payment_method_asc', desc: 'payment_method_desc' },
};

const platformInvoiceSortColumns = ['invoiceDate', 'recipientName', 'paymentType', 'netAmount', 'total', 'paymentStatus'] as const;
type PlatformInvoiceSortColumn = (typeof platformInvoiceSortColumns)[number];
const platformInvoiceSortMapping: SortMapping<PlatformInvoiceSortColumn, PlatformInvoiceSortOption> = {
  invoiceDate: { asc: 'invoice_date_asc', desc: 'invoice_date_desc' },
  recipientName: { asc: 'recipient_asc', desc: 'recipient_desc' },
  paymentType: { asc: 'payment_type_asc', desc: 'payment_type_desc' },
  netAmount: { asc: 'invoice_net_amount_asc', desc: 'invoice_net_amount_desc' },
  total: { asc: 'invoice_total_asc', desc: 'invoice_total_desc' },
  paymentStatus: { asc: 'payment_status_asc', desc: 'payment_status_desc' },
};

const workInvoiceSortColumns = ['issuedAt', 'baseAmount', 'amount', 'status'] as const;
type WorkInvoiceSortColumn = (typeof workInvoiceSortColumns)[number];
const workInvoiceSortMapping: SortMapping<WorkInvoiceSortColumn, WorkInvoiceSortOption> = {
  issuedAt: { asc: 'issued_at_asc', desc: 'issued_at_desc' },
  baseAmount: { asc: 'base_amount_asc', desc: 'base_amount_desc' },
  amount: { asc: 'amount_asc', desc: 'amount_desc' },
  status: { asc: 'status_asc', desc: 'status_desc' },
};

const OperatorsAccountingPage = () => {
  const { t, i18n } = useTranslation('operator');
  const convex = useConvex();

  // Filter state
  const [filters, setFilters] = React.useState<FilterValues>({
    status: 'all',
    dateFrom: '', // empty = no filter
    dateTo: '',
    userSearch: '',
    hiddenState: 'active',
  });

  // Tab state
  const [activeTab, setActiveTab] = React.useState<AccountingTabType>('transactions');

  // Pagination state
  const [transactionsPage, setTransactionsPage] = React.useState(0);
  const [invoicesPage, setInvoicesPage] = React.useState(0);
  const [workInvoicesPage, setWorkInvoicesPage] = React.useState(0);
  const [transactionsSorting, setTransactionsSorting] = React.useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [invoicesSorting, setInvoicesSorting] = React.useState<SortingState>([{ id: 'invoiceDate', desc: true }]);
  const [workInvoicesSorting, setWorkInvoicesSorting] = React.useState<SortingState>([{ id: 'issuedAt', desc: true }]);

  // Export state
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportType, setExportType] = React.useState<AccountingExportType>('transactions');
  const [visibilityDialogTarget, setVisibilityDialogTarget] = React.useState<BookingVisibilityDialogTarget | null>(null);
  const [isVisibilityMutationRunning, setIsVisibilityMutationRunning] = React.useState(false);
  const [reconcilingPaymentIds, setReconcilingPaymentIds] = React.useState<Set<Id<'payments'>>>(() => new Set());

  // Regeneration state
  const [isRegenerating, setIsRegenerating] = React.useState(false);
  const regenerateInvoices = useAction(api.app.payments.invoiceActions.regenerateMissingInvoices);
  const repairPlatformFeeInvoice = useAction(api.app.payments.invoiceActions.repairPlatformFeeInvoice);
  const correctPlatformFeeInvoiceRecipient = useAction(api.app.payments.invoiceActions.correctPlatformFeeInvoiceRecipient);
  const hidePaymentBooking = useMutation(api.app.index.hidePaymentBooking);
  const restorePaymentBooking = useMutation(api.app.index.restorePaymentBooking);
  const hideWorkInvoiceBooking = useMutation(api.app.index.hideWorkInvoiceBooking);
  const restoreWorkInvoiceBooking = useMutation(api.app.index.restoreWorkInvoiceBooking);
  const markInvoiceTransferPaymentReceived = useMutation(api.app.index.markInvoiceTransferPaymentReceived);
  const reconcilePaymentWithMollie = useAction(api.app.payments.webhookActions.reconcilePaymentWithMollie);

  // Regenerate handler
  const handleRegenerateInvoices = async (): Promise<void> => {
    setIsRegenerating(true);
    try {
      const result = await regenerateInvoices({});
      if (result.generated > 0) {
        toast.success(t('accounting.toasts.invoicesGenerated', { count: result.generated }));
      } else if (result.skipped === result.processed) {
        toast.info(t('accounting.toasts.invoicesAlreadyPresent'));
      } else if (result.errors.length > 0) {
        toast.warning(t('accounting.toasts.generateErrors', { count: result.errors.length }));
      }
      console.log('[regenerate] Result:', result);
    } catch (error) {
      console.error('Regeneration error:', error);
      toast.error(t('accounting.toasts.generateFailed'));
    } finally {
      setIsRegenerating(false);
    }
  };

  // Parse filter dates to timestamps
  const dateFrom = filters.dateFrom ? startOfBerlinDay(dateKeyToBerlinReference(filters.dateFrom)) : undefined;
  const dateTo = filters.dateTo ? endOfBerlinDay(dateKeyToBerlinReference(filters.dateTo)) : undefined;
  const transactionsSortBy = mapSortingToSortKey(transactionsSorting, transactionSortColumns, transactionSortMapping, 'created_at_desc');
  const invoicesSortBy = mapSortingToSortKey(invoicesSorting, platformInvoiceSortColumns, platformInvoiceSortMapping, 'invoice_date_desc');
  const workInvoicesSortBy = mapSortingToSortKey(workInvoicesSorting, workInvoiceSortColumns, workInvoiceSortMapping, 'issued_at_desc');

  // Overview metrics query
  const stats = useQuery(api.app.operatorAccounting.getAccountingOverview, { hiddenState: filters.hiddenState });

  // Build query args for transactions (with exactOptionalPropertyTypes compliance)
  const transactionsArgs = {
    page: transactionsPage,
    pageSize: 20,
    hiddenState: filters.hiddenState,
    sortBy: transactionsSortBy,
    ...(filters.status !== 'all' && {
      status: filters.status as PaymentStatus,
    }),
    ...(dateFrom !== undefined && { dateFrom }),
    ...(dateTo !== undefined && { dateTo }),
  };

  const transactionsData = useQuery(api.app.operatorAccounting.listTransactions, transactionsArgs);

  // Build query args for platform invoices
  const invoicesArgs = {
    page: invoicesPage,
    pageSize: 20,
    hiddenState: filters.hiddenState,
    sortBy: invoicesSortBy,
    ...(filters.status !== 'all' && {
      status: filters.status as PaymentStatus,
    }),
    ...(dateFrom !== undefined && { dateFrom }),
    ...(dateTo !== undefined && { dateTo }),
  };

  const invoicesData = useQuery(api.app.operatorAccounting.listPlatformInvoices, invoicesArgs);
  const missingInvoiceQueryArgs =
    activeTab === 'invoices'
      ? {
          hiddenState: filters.hiddenState,
          ...(filters.status !== 'all' && { status: filters.status as PaymentStatus }),
          ...(dateFrom !== undefined && { dateFrom }),
          ...(dateTo !== undefined && { dateTo }),
        }
      : null;
  const missingProviderInvoices = usePaginatedQuery(api.app.operatorAccounting.listMissingPlatformInvoiceCandidates, missingInvoiceQueryArgs ? { ...missingInvoiceQueryArgs, source: 'provider' } : 'skip', { initialNumItems: 50 });
  const missingSubstituteInvoices = usePaginatedQuery(api.app.operatorAccounting.listMissingPlatformInvoiceCandidates, missingInvoiceQueryArgs ? { ...missingInvoiceQueryArgs, source: 'substitute' } : 'skip', { initialNumItems: 50 });
  const missingLegacyProviderInvoices = usePaginatedQuery(api.app.operatorAccounting.listMissingPlatformInvoiceCandidates, missingInvoiceQueryArgs ? { ...missingInvoiceQueryArgs, source: 'legacy_provider' } : 'skip', { initialNumItems: 50 });
  const missingInvoiceQueries = [missingProviderInvoices, missingSubstituteInvoices, missingLegacyProviderInvoices];
  const missingInvoiceCandidates = missingInvoiceQueries.flatMap((query) => query.results).sort((a, b) => b.paymentDate - a.paymentDate);
  const missingInvoicePaginationStatus = missingInvoiceQueries.some((query) => query.status === 'LoadingFirstPage')
    ? 'LoadingFirstPage'
    : missingInvoiceQueries.some((query) => query.status === 'LoadingMore')
      ? 'LoadingMore'
      : missingInvoiceQueries.some((query) => query.status === 'CanLoadMore')
        ? 'CanLoadMore'
        : 'Exhausted';

  // Build query args for work invoices
  const workInvoiceStatus = ['draft', 'issued', 'paid', 'superseded'].includes(filters.status) ? (filters.status as 'draft' | 'issued' | 'paid' | 'superseded') : undefined;
  const workInvoicesArgs = {
    page: workInvoicesPage,
    pageSize: 20,
    hiddenState: filters.hiddenState,
    sortBy: workInvoicesSortBy,
    ...(workInvoiceStatus !== undefined && { status: workInvoiceStatus }),
    ...(dateFrom !== undefined && { dateFrom }),
    ...(dateTo !== undefined && { dateTo }),
  };

  const workInvoicesData = useQuery(api.app.operatorAccounting.listWorkInvoices, workInvoicesArgs);

  // CSV Export handler
  const handleExportCSV = async (): Promise<void> => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast.error(t('accounting.toasts.selectDateRange'));
      return;
    }

    setIsExporting(true);

    try {
      const exportData = await convex.query(api.app.operatorAccounting.getAccountingExportData, {
        type: exportType,
        dateFrom: startOfBerlinDay(dateKeyToBerlinReference(filters.dateFrom)),
        dateTo: endOfBerlinDay(dateKeyToBerlinReference(filters.dateTo)),
        hiddenState: filters.hiddenState,
      });

      if (!exportData) {
        toast.error(t('accounting.toasts.noExportData'));
        setIsExporting(false);
        return;
      }

      // Flatten the export data structure
      type ExportRow = Record<string, string | number | boolean | null | undefined>;
      let dataToExport: ExportRow[] = [];
      if (exportType === 'transactions' && exportData.transactions) {
        dataToExport = exportData.transactions as ExportRow[];
      } else if (exportType === 'invoices' && exportData.invoices) {
        dataToExport = exportData.invoices as ExportRow[];
      } else if (exportType === 'workInvoices' && exportData.workInvoices) {
        dataToExport = exportData.workInvoices as ExportRow[];
      } else if (exportType === 'all') {
        // Combine all data with section headers
        dataToExport = [
          ...((exportData.transactions || []) as ExportRow[]).map((tRow) => ({ Typ: t('accounting.export.types.transaction'), ...tRow })),
          ...((exportData.invoices || []) as ExportRow[]).map((iRow) => ({ Typ: t('accounting.export.types.platformFee'), ...iRow })),
          ...((exportData.workInvoices || []) as ExportRow[]).map((wRow) => ({ Typ: t('accounting.export.types.workInvoice'), ...wRow })),
        ];
      }

      // Platform invoices inherit their visibility from the linked payment, so in the
      // combined ('all') export a hidden payment and its inherited invoice are the same
      // Buchung. Exclude the inherited invoice count there to avoid double-counting.
      const excludedHiddenCount =
        exportType === 'all'
          ? exportData.reconciliation.hiddenTransactionsCount + exportData.reconciliation.hiddenWorkInvoicesCount
          : exportData.reconciliation.hiddenTransactionsCount + exportData.reconciliation.hiddenInvoicesCount + exportData.reconciliation.hiddenWorkInvoicesCount;

      if (dataToExport.length === 0) {
        if (filters.hiddenState === 'active' && excludedHiddenCount > 0) {
          toast.info(t('accounting.toasts.exportExcludedHidden', { count: excludedHiddenCount }));
        }
        toast.error(t('accounting.toasts.noExportData'));
        setIsExporting(false);
        return;
      }

      // Convert to CSV
      const headers = Object.keys(dataToExport[0]);
      const csvRows = [
        headers.join(','), // header row
        ...dataToExport.map((row) =>
          headers
            .map((header) => {
              const value = row[header];
              // Escape commas and quotes in values
              const stringValue = value !== null && value !== undefined ? String(value) : '';
              return stringValue.includes(',') || stringValue.includes('"') ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
            })
            .join(',')
        ),
      ];

      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      // Download file
      const link = document.createElement('a');
      link.href = url;
      const dateStr = i18n.language.startsWith('en') ? format(new Date(), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd', { locale: de });
      link.download = `${t('accounting.export.filePrefix')}_${exportType}_${dateStr}.csv`;
      link.click();

      URL.revokeObjectURL(url);
      if (filters.hiddenState === 'active') {
        if (excludedHiddenCount > 0) {
          toast.info(t('accounting.toasts.exportExcludedHidden', { count: excludedHiddenCount }));
        }
      }
      toast.success(t('accounting.toasts.exportDownloaded'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('accounting.toasts.exportFailed'));
    } finally {
      setIsExporting(false);
    }
  };

  // PDF download handlers
  const handleDownloadPlatformInvoicePDF = async (invoiceId: Id<'invoices'>): Promise<void> => {
    try {
      toast.info(t('accounting.toasts.pdfLoading'));

      // Find invoice in current data (already loaded)
      const targetInvoice = invoicesData?.invoices.find((inv: { _id: Id<'invoices'>; pdfFileId?: Id<'_storage'> }) => inv._id === invoiceId);

      if (!targetInvoice) {
        toast.error(t('accounting.toasts.invoiceNotFound'));
        return;
      }

      if (!targetInvoice.pdfFileId) {
        toast.error(t('accounting.toasts.pdfUnavailable'));
        return;
      }

      // Get signed URL for PDF
      const url = await convex.query(api.app.storage.queries.getStorageUrl, {
        storageId: targetInvoice.pdfFileId,
      });

      if (!url) {
        toast.error(t('accounting.toasts.pdfLoadFailed'));
        return;
      }

      // Open PDF in new tab
      window.open(url, '_blank');

      toast.success(t('accounting.toasts.pdfOpened'));
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(t('accounting.toasts.pdfDownloadFailed'));
    }
  };

  const handleDownloadWorkInvoicePDF = async (invoiceId: Id<'workInvoices'>): Promise<void> => {
    try {
      toast.info(t('accounting.toasts.pdfLoading'));

      // Find work invoice in current data (already loaded)
      const targetInvoice = workInvoicesData?.workInvoices.find((inv: { _id: Id<'workInvoices'> }) => inv._id === invoiceId);

      if (!targetInvoice) {
        toast.error(t('accounting.toasts.workInvoiceNotFound'));
        return;
      }

      if (!targetInvoice.storageId) {
        toast.error(t('accounting.toasts.pdfUnavailable'));
        return;
      }

      // Get signed URL for PDF
      const url = await convex.query(api.app.storage.queries.getStorageUrl, {
        storageId: targetInvoice.storageId,
      });

      if (!url) {
        toast.error(t('accounting.toasts.pdfLoadFailed'));
        return;
      }

      // Open PDF in new tab
      window.open(url, '_blank');

      toast.success(t('accounting.toasts.pdfOpened'));
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(t('accounting.toasts.pdfDownloadFailed'));
    }
  };

  const handleMarkInvoiceTransferPaymentReceived = async (paymentId: Id<'payments'>): Promise<void> => {
    const result = await markInvoiceTransferPaymentReceived({ paymentId });
    if (result.reconciliationException) {
      toast.warning(t('accounting.toasts.paymentReceivedWithException'));
    } else {
      toast.success(t(result.alreadySettled ? 'accounting.toasts.paymentAlreadyReceived' : 'accounting.toasts.paymentReceived'));
    }
  };

  const handleReconcilePayment = async (transaction: Transaction): Promise<void> => {
    await reconcileAccountingPayment({ transaction, reconcile: reconcilePaymentWithMollie, setReconcilingPaymentIds, t });
  };

  const handleRepairPlatformFeeInvoice = async (paymentId: Id<'payments'>, refreshIssuerDetails: boolean): Promise<void> => {
    const result = await repairPlatformFeeInvoice({ paymentId, refreshIssuerDetails });
    toast.success(t(result.status === 'generated' ? 'accounting.toasts.invoiceRepairGenerated' : 'accounting.toasts.invoiceRepairAlreadyAvailable', { invoiceNumber: result.invoiceNumber }));
  };

  const handleCorrectPlatformFeeInvoice = async (paymentId: Id<'payments'>, sendEmail: boolean): Promise<void> => {
    const result = await correctPlatformFeeInvoiceRecipient({ paymentId, sendEmail });
    const values = { invoiceNumber: result.invoiceNumber, recipientName: result.recipientName };
    if (result.status === 'corrected') toast.success(t(result.emailQueued ? 'accounting.toasts.invoiceCorrectedAndSent' : 'accounting.toasts.invoiceCorrected', values));
    else if (result.emailQueued) toast.success(t('accounting.toasts.invoiceCorrectionResent', values));
    else toast.info(t('accounting.toasts.invoiceCorrectionUnchanged', values));
  };

  // Handle filter changes and reset pagination
  const handleFiltersChange = (newFilters: FilterValues) => {
    setFilters(newFilters);
    setTransactionsPage(0);
    setInvoicesPage(0);
    setWorkInvoicesPage(0);
  };

  const handleTransactionsSortingChange = React.useCallback<OnChangeFn<SortingState>>((updater) => {
    setTransactionsSorting((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return didSortingChange(current, next) ? next : current;
    });
    setTransactionsPage(0);
  }, []);

  const handleInvoicesSortingChange = React.useCallback<OnChangeFn<SortingState>>((updater) => {
    setInvoicesSorting((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return didSortingChange(current, next) ? next : current;
    });
    setInvoicesPage(0);
  }, []);

  const handleWorkInvoicesSortingChange = React.useCallback<OnChangeFn<SortingState>>((updater) => {
    setWorkInvoicesSorting((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      return didSortingChange(current, next) ? next : current;
    });
    setWorkInvoicesPage(0);
  }, []);

  const handleOpenTransactionVisibilityDialog = (mode: BookingVisibilityDialogMode, transaction: Transaction): void => {
    setVisibilityDialogTarget({ type: 'transaction', mode, transaction });
  };

  const handleOpenWorkInvoiceVisibilityDialog = (mode: BookingVisibilityDialogMode, invoice: WorkInvoice): void => {
    setVisibilityDialogTarget({ type: 'workInvoice', mode, invoice });
  };

  const handleConfirmVisibilityDialog = async ({ reasonCode, note }: { reasonCode: HideBookingReasonCode | RestoreBookingReasonCode; note?: string }): Promise<void> => {
    if (!visibilityDialogTarget) return;

    setIsVisibilityMutationRunning(true);
    try {
      if (visibilityDialogTarget.type === 'transaction') {
        if (visibilityDialogTarget.mode === 'hide') {
          await hidePaymentBooking({ paymentId: visibilityDialogTarget.transaction._id, reasonCode: reasonCode as HideBookingReasonCode, ...(note && { note }) });
          toast.success(t('accounting.toasts.hideSuccess'));
        } else {
          await restorePaymentBooking({ paymentId: visibilityDialogTarget.transaction._id, reasonCode: reasonCode as RestoreBookingReasonCode, ...(note && { note }) });
          toast.success(t('accounting.toasts.restoreSuccess'));
        }
        setTransactionsPage(0);
        setInvoicesPage(0);
      } else if (visibilityDialogTarget.mode === 'hide') {
        await hideWorkInvoiceBooking({ workInvoiceId: visibilityDialogTarget.invoice._id, reasonCode: reasonCode as HideBookingReasonCode, ...(note && { note }) });
        toast.success(t('accounting.toasts.hideSuccess'));
        setWorkInvoicesPage(0);
      } else {
        await restoreWorkInvoiceBooking({ workInvoiceId: visibilityDialogTarget.invoice._id, reasonCode: reasonCode as RestoreBookingReasonCode, ...(note && { note }) });
        toast.success(t('accounting.toasts.restoreSuccess'));
        setWorkInvoicesPage(0);
      }

      setVisibilityDialogTarget(null);
    } catch (error) {
      console.error('Booking visibility error:', error);
      toast.error(visibilityDialogTarget.mode === 'hide' ? t('accounting.toasts.hideFailed') : t('accounting.toasts.restoreFailed'));
    } finally {
      setIsVisibilityMutationRunning(false);
    }
  };

  const visibilityDialogLabel =
    visibilityDialogTarget?.type === 'transaction' ? visibilityDialogTarget.transaction.molliePaymentId : visibilityDialogTarget?.type === 'workInvoice' ? visibilityDialogTarget.invoice.dutyTitle || visibilityDialogTarget.invoice._id : '';

  return (
    <div className='p-4 space-y-6'>
      <h1 className='text-2xl font-semibold'>{t('accounting.title')}</h1>

      {/* Overview Cards */}
      <AccountingOverviewCards stats={stats} isLoading={stats === undefined} />

      {/* Filters */}
      <AccountingFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        statusOptions={[
          { value: 'all', label: t('accounting.statusOptions.all') },
          { value: 'pending', label: t('accounting.statusOptions.pending') },
          { value: 'succeeded', label: t('accounting.statusOptions.succeeded') },
          { value: 'failed', label: t('accounting.statusOptions.failed') },
          { value: 'expired', label: t('accounting.statusOptions.expired') },
          { value: 'canceled', label: t('accounting.statusOptions.canceled') },
          { value: 'refunded', label: t('accounting.statusOptions.refunded') },
        ]}
      />

      {/* Export Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t('accounting.export.title')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid gap-3 sm:grid-cols-2'>
            <div>
              <label className='text-sm font-medium mb-1.5 block'>{t('accounting.export.typeLabel')}</label>
              <Select value={exportType} onValueChange={(v: string | null) => setExportType(v as AccountingExportType)}>
                <SelectTrigger aria-label={t('accounting.export.typeLabel')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='transactions'>{t('accounting.export.options.transactions')}</SelectItem>
                  <SelectItem value='invoices'>{t('accounting.export.options.invoices')}</SelectItem>
                  <SelectItem value='workInvoices'>{t('accounting.export.options.workInvoices')}</SelectItem>
                  <SelectItem value='all'>{t('accounting.export.options.all')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className='flex items-end'>
              <Button onClick={handleExportCSV} disabled={isExporting || !filters.dateFrom || !filters.dateTo} className='w-full'>
                {isExporting ? (
                  <>{t('accounting.export.exporting')}</>
                ) : (
                  <>
                    <Download className='h-4 w-4 mr-2' />
                    {t('accounting.export.exportCsv')}
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className='grid gap-3 sm:grid-cols-2'>
            <div>
              <label className='text-sm font-medium mb-1.5 block'>{t('accounting.regenerate.title')}</label>
              <p className='text-xs text-muted-foreground mb-2'>{t('accounting.regenerate.description')}</p>
            </div>
            <div className='flex items-end'>
              <Button onClick={handleRegenerateInvoices} disabled={isRegenerating} variant='secondary' className='w-full'>
                {isRegenerating ? (
                  <>{t('accounting.regenerate.running')}</>
                ) : (
                  <>
                    <Icons.RefreshCw className='h-4 w-4 mr-2' />
                    {t('accounting.regenerate.action')}
                  </>
                )}
              </Button>
            </div>
          </div>

          {(!filters.dateFrom || !filters.dateTo) && <p className='text-sm text-muted-foreground'>{t('accounting.export.hintSelectDateRange')}</p>}
        </CardContent>
      </Card>

      {/* Tabbed Tables */}
      <Tabs value={activeTab} onValueChange={(v: string | null) => setActiveTab(v as typeof activeTab)}>
        <TabsList>
          <TabsTrigger value='transactions'>{t('accounting.tabs.transactions')}</TabsTrigger>
          <TabsTrigger value='invoices'>{t('accounting.tabs.invoices')}</TabsTrigger>
          <TabsTrigger value='workInvoices'>{t('accounting.tabs.workInvoices')}</TabsTrigger>
        </TabsList>

        <TabsContent value='transactions'>
          <TransactionsTable
            transactions={transactionsData?.transactions}
            total={transactionsData?.total || 0}
            page={transactionsPage}
            pageSize={20}
            onPageChange={setTransactionsPage}
            sorting={transactionsSorting}
            onSortingChange={handleTransactionsSortingChange}
            onHide={(transaction) => handleOpenTransactionVisibilityDialog('hide', transaction)}
            onRestore={(transaction) => handleOpenTransactionVisibilityDialog('restore', transaction)}
            onReconcile={handleReconcilePayment}
            reconcilingPaymentIds={reconcilingPaymentIds}
            isLoading={transactionsData === undefined}
          />
        </TabsContent>

        <TabsContent value='invoices'>
          <MissingPlatformInvoices
            candidates={missingInvoiceCandidates}
            paginationStatus={missingInvoicePaginationStatus}
            onLoadMore={() => {
              missingInvoiceQueries.forEach((query) => {
                if (query.status === 'CanLoadMore') query.loadMore(50);
              });
            }}
            onRepair={handleRepairPlatformFeeInvoice}
          />
          <InvoicesTable
            invoices={invoicesData?.invoices}
            total={invoicesData?.total || 0}
            page={invoicesPage}
            pageSize={20}
            onPageChange={setInvoicesPage}
            sorting={invoicesSorting}
            onSortingChange={handleInvoicesSortingChange}
            onDownloadPDF={handleDownloadPlatformInvoicePDF}
            onMarkPaymentReceived={handleMarkInvoiceTransferPaymentReceived}
            onRepairInvoice={handleRepairPlatformFeeInvoice}
            onCorrectInvoice={handleCorrectPlatformFeeInvoice}
            isLoading={invoicesData === undefined}
          />
        </TabsContent>

        <TabsContent value='workInvoices'>
          <WorkInvoicesTable
            workInvoices={workInvoicesData?.workInvoices}
            total={workInvoicesData?.total || 0}
            page={workInvoicesPage}
            pageSize={20}
            onPageChange={setWorkInvoicesPage}
            sorting={workInvoicesSorting}
            onSortingChange={handleWorkInvoicesSortingChange}
            onDownloadPDF={handleDownloadWorkInvoicePDF}
            onHide={(invoice) => handleOpenWorkInvoiceVisibilityDialog('hide', invoice)}
            onRestore={(invoice) => handleOpenWorkInvoiceVisibilityDialog('restore', invoice)}
            isLoading={workInvoicesData === undefined}
          />
        </TabsContent>
      </Tabs>

      <HideBookingDialog
        open={visibilityDialogTarget !== null}
        mode={visibilityDialogTarget?.mode ?? 'hide'}
        targetLabel={visibilityDialogLabel}
        isSubmitting={isVisibilityMutationRunning}
        onOpenChange={(open) => {
          if (!open && !isVisibilityMutationRunning) setVisibilityDialogTarget(null);
        }}
        onConfirm={handleConfirmVisibilityDialog}
      />
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/accounting')({
  component: OperatorsAccountingPage,
});
